import type { ChangeEventHandler } from 'react'
import { useIntl } from '@/intl'
import TopNavigationBarItem from '@/scopes/common/components/Header/TopNavigationBarItem'

import { useDebouncedCallback } from '@/scopes/common/components/hooks/useDebouncedCallback'
import { HighlightedTextUds as HighlightedText } from '@/scopes/search/components/PartiallyHighlightedTextUds'
import { useLiveSearch } from '@/scopes/search/search/domain/live-search/hooks'
import { useLiveSearchStore } from '@/scopes/search/search/infrastructure/live-search/store'

import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, IndeterminateProgressCircle, Popover, SearchField, Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { Category } from './Category'

const Root = styled(Popover.Root)`
  [data-radix-popper-content-wrapper] {
    z-index: 99 !important;
  }
`

const PopoverContent = styled(Popover.Content)`
  @media (max-width: ${tokPx('primitives_breakpoint_s_max')}) {
    ${Popover.Body} {
      overflow-y: hidden;
    }
  }
`

const BodyWrapper = styled.div`
  position: relative;
  &:not(:empty) {
    height: 100%;
    overflow-y: auto;
    border-radius: ${tokPx('primitives_corner_radius_xxl')};
    margin-top: ${space('primitives_space_8')};
    padding-top: ${space('primitives_space_16')};
  }
`

const Wrapper = styled.div`
  &:not(:empty) {
    padding-top: ${space('primitives_space_16')};
    margin: ${space('primitives_space_16')};
    margin-top: 0;
  }
`

const Trigger = styled(Popover.Trigger)`
  padding: ${space('primitives_space_8')};
  border-radius: ${tokPx('semantics_buttons_m_corner_radius')};
  background: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: ${tok('semantics_content_color')};
    background: ${tok('primitives_color_neutral_100')};
  }

  &[data-state='open'] {
    background: ${tok('primitives_color_neutral_150')};
  }
`

const Spinner = styled(IndeterminateProgressCircle)`
  color: ${tok('primitives_color_red_500')};
`

export function Loader() {
  const { isFetching, isLoading } = useLiveSearch()
  const { query } = useLiveSearchStore()
  if (isFetching || isLoading) {
    return (
      <>
        <HighlightedText>{query}</HighlightedText>
        {' ... '}
        <Spinner />
      </>
    )
  }
  return null
}

export function Body() {
  const { data, isFetching, isLoading } = useLiveSearch()
  if (!isFetching && !isLoading && !isEmpty(data) && data) {
    return <Results />
  }
  return null
}

export function NoResults() {
  const intl = useIntl()
  const { data, isFetching, isLoading } = useLiveSearch()
  const { query } = useLiveSearchStore()

  if (!isFetching && !isLoading && isEmpty(data) && query) {
    return (
      <>
        <Typography>{intl.formatMessage({ id: 'live-search_no_results' })}</Typography>
        {' '}
        <HighlightedText>{query}</HighlightedText>
      </>
    )
  }
  return null
}

function Results() {
  const intl = useIntl()
  const { data } = useLiveSearch()

  return (
    <>
      {data?.flightTickets && data.flightTickets.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_flighttickets' })}
          icon="AirplaneToTop"
          items={data.flightTickets}
        />
      )}
      {data?.destinations && data?.destinations.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_destinations' })}
          icon="MapPin"
          items={data.destinations}
        />
      )}
      {data?.accommodations && data.accommodations.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_accommodations' })}
          icon="House"
          items={data.accommodations}
        />
      )}
      {data?.information && data.information.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_information' })}
          icon="InformationInCircle"
          items={data.information}
        />
      )}
    </>
  )
}

export function LiveSearch() {
  const [open, setOpen] = React.useState(false)
  const { setQuery } = useLiveSearchStore()
  const debouncedSetQuery = useDebouncedCallback(setQuery, 500)
  const intl = useIntl()

  const onOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen)
      if (!newOpen) {
        setQuery('')
      }
    },
    [setOpen],
  )

  const handleOnChange: ChangeEventHandler = (e) => {
    const { value } = e.target as HTMLInputElement
    debouncedSetQuery(value)
  }

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Trigger>
        <TopNavigationBarItem>
          <Icon glyph="Magnifier" />
        </TopNavigationBarItem>
      </Trigger>
      <PopoverContent position="top-left" hasPadding fullHeight closeText={intl.formatMessage({ id: 'close' })}>
        <SearchField onChange={handleOnChange} placeholder={intl.formatMessage({ id: 'live-search_search_button_text' })} />
        <BodyWrapper>
          <Body />
        </BodyWrapper>
        <Wrapper>
          <Loader />
          <NoResults />
        </Wrapper>
      </PopoverContent>
    </Root>
  )
}
